import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { QuoteApproval } from '../core/models/quote-approval.model';
import { map, Observable, of } from 'rxjs';
import { PlanApiResponse } from '../core/models/plan-api-response';
import { PlanApiFilterRequest } from '../core/models/plan-api-filter-request';
import { OpenQuoteModel } from '../core/models/open-quote.model';
import {
  GetPlansResponseModel,
  PlanFilterRequestModel,
  Quote,
  SummaryBenefitModel,
  PlanModel,
  PaymentSubmissionModel,
  PrescriptionResponseModel,
  ProviderResponseModel,
  AddonsFromQuoteModel,
} from '../core';
import { EsignModel } from '../core/models/esign.model';

@Injectable({ providedIn: 'root' })
export class OpenQuoteService extends ApiService {
  constructor(http: HttpClient) {
    super(http, 'SavedQuote');
  }

  getQuote(quoteID: string): Observable<OpenQuoteModel> {
    return this.http.get<{ quote: OpenQuoteModel }>(`${this.route()}/${quoteID}`).pipe(map((response) => response.quote));
  }

  saveQuote(data: OpenQuoteModel): Observable<{ quote: OpenQuoteModel }> {
    return this.http.post<{ quote: OpenQuoteModel }>(this.route(), data);
  }

  quoteApproval(data: QuoteApproval) {
    return this.http.post(`${this.route()}/quoteaproval`, data);
  }

  quoteView(quoteID: string) {
    return this.http.post<Quote>(`${this.route()}/QuoteView/${quoteID}`, null);
  }


  quotePlanBenefits(quoteID: string, planID: string) {
    return this.http.get<SummaryBenefitModel[]>(`${this.baseUrl}/Quote/Benefits/${quoteID}/${planID}`);
  }

  getRecommendedPlans(quoteId: string, filter: PlanFilterRequestModel = {} as PlanFilterRequestModel) {
    return this.http.get<any>(`${this.route()}/${quoteId}/plans/recommendations`, {
      params: this.toParams(filter),
    });
  }

  getPlans(customerID: string, dependentsIDs: string[], planType: string, filter: PlanApiFilterRequest): Observable<PlanApiResponse> {
    return this.http.post<PlanApiResponse>(`${this.route()}/GetPlansForCustomer`, {
      customerID,
      DependentsIds: dependentsIDs,
      PlanType: planType,
      planFilterRequestModel: filter,
    });
  }

  getAllPlans(quoteID: string, filter: PlanFilterRequestModel): Observable<GetPlansResponseModel> {
    return this.http.get<GetPlansResponseModel>(`${this.route()}/${quoteID}/plans`, {
      params: this.toParams(filter),
    });
  }

  getPlan(quoteID: string, planID: string): Observable<any> {
    return this.http.get<any>(`${this.route()}/${quoteID}/plans/${planID}`);
  }

  isPlanAvailable(quoteID: string, planID: string): Observable<{ available: boolean }> {
    return this.http.get<{ available: boolean }>(`${this.route()}/${quoteID}/plans/${planID}/available`);
  }

  refreshQuote(quoteID: string): Observable<{ summary: any; planAvailable: boolean; autoAssociatedPlanId?: string | null }> {
    return this.http.post<{ summary: any; planAvailable: boolean; autoAssociatedPlanId?: string | null }>(`${this.route()}/${quoteID}/refresh`, {});
  }

  createProposal(primaryQuoteId: string, payload: { planIdentifiers: string[], effectiveDate: Date }): Observable<{ proposalId: string; message: string }> {
    return this.http.post<{ proposalId: string; message: string }>(`${this.route()}/proposal/${primaryQuoteId}`, payload);
  }

  getPromoGuideUrl(groupId: string, planId: string, state: string): Observable<string> {
    return this.http.get(`${this.route()}/promoguide/${groupId}-${planId}/${state}`, {
      responseType: 'text',
    });
  }

  getRecommendedAddOns(quoteId: string, plan: { planIdentifier: string }): Observable<PlanModel[]> {
    return this.http.post<PlanModel[]>(`${this.route()}/getRecommendedAddOns/${quoteId}`, plan);
  }

  getProductsFromProposal(quoteId: string): Observable<AddonsFromQuoteModel> {
    return this.http.get<AddonsFromQuoteModel>(`${this.route()}/getProductsFromProposal/${quoteId}`);
  }

  createEsignAndSendSMS(data: PaymentSubmissionModel): Observable<void> {
    return this.http.post<void>(`${this.route()}/createEsignAndSendSMS`, data);
  }

  createEsignAndSendEmail(data: PaymentSubmissionModel): Observable<void> {
    return this.http.post<void>(`${this.route()}/createEsignAndSendEmail`, data);
  }

  getEsignUrl(caseId: string): Observable<string> {
    return this.http.post<string>(`${this.route()}/getEsignUrl/${caseId}`, {});
  }

  getEsignInfo(quoteId: string): Observable<EsignModel> {
    return this.http.post<EsignModel>(`${this.route()}/getEsignInfo/${quoteId}`, {});
  }

  searchPrescriptions(query: string, year?: number): Observable<PrescriptionResponseModel> {
    let params = new HttpParams().set('q', query);
    if (year) {
      params = params.set('year', year.toString());
    }

    return this.http.get<PrescriptionResponseModel>(`${this.route()}/prescription-drugs/search`, { params });
  }

  searchProviders(q: string, zipcode: string): Observable<ProviderResponseModel> {
    const params = new HttpParams().set('q', q).set('zipcode', zipcode);

    return this.http.get<ProviderResponseModel>(`${this.route()}/provider/search`, { params });
  }

  getStateByZip(zip: string): Observable<{ code: string; name: string; counties: { name: string; fips: string; state: string; stateName: string }[] }> {
    let params = new HttpParams().set('zip', zip);
    return this.http.get<{ code: string; name: string; counties: { name: string; fips: string; state: string; stateName: string }[] }>(`${this.route()}/state/${zip}`, {
      params,
    });
  }

  sendCheckoutBySms(quoteId: string): Observable<void> {
    return this.http.post<void>(`${this.route()}/sendCheckoutBySms/${quoteId}`, {});
  }

  sendBrochureBySms(quoteId: string, brochureUrl?: string): Observable<void> {
    return this.http.post<void>(`${this.route()}/sendBrochureBySms/${quoteId}`, JSON.stringify(brochureUrl || ''), { headers: { 'Content-Type': 'application/json' } });
  }

  sendCheckoutByEmail(quoteId: string): Observable<void> {
    return this.http.post<void>(`${this.route()}/sendCheckoutByEmail/${quoteId}`, {});
  }

  associatePlans(quoteId: string, planIdentifier: string): Observable<{ message: string; quote: OpenQuoteModel }> {
    const payload = { planIdentifier };
    return this.http.post<{ message: string; quote: OpenQuoteModel }>(`${this.route()}/${quoteId}/plans/associate`, payload);
  }

  protected toParams(obj: any): { [param: string]: string | string[] } {
    const params: any = {};
    Object.entries(obj).forEach(([key, value]) => {
      if (value === undefined || value === null) return;

      params[key] = Array.isArray(value) ? value.map((v) => v.toString()) : value.toString();
    });
    return params;
  }
}
